<template>
	<div class="calendar-view">
		<!-- https://github.com/richardtallent/vue-simple-calendar?tab=readme-ov-file#installation-and-usage -->
		<calendar-view :show-date="computedShowDate" :disable-past="true"
			:period-changed-callback="periodChangeCallback" :items="calendarItems" :show-times="true"
			class="theme-default" @click-item="onClickItem" :starting-day-of-week="1"
			:time-format-options="{ hour: '2-digit', minute: '2-digit', hour12: false }" :next-year="false">
			<template #header="{ headerProps }">
				<calendar-view-header @input="setShowDate" :header-props />
			</template>
		</calendar-view>
	</div>
</template>

<script>
import { CalendarView, CalendarViewHeader } from "vue-simple-calendar"
import "vue-simple-calendar/dist/style.css"

// The next line is optional themes
import "vue-simple-calendar/dist/css/default.css"

export default {
	name: 'EventCalendar',
	props: {
		events: Array
	},
	components: {
		CalendarView,
		CalendarViewHeader
	},
	data() {
		return {
			useHardLink: ('1' === process.env.VUE_APP_USE_SINGLE_POST_HARD_LINK || false),
			usedColours: new Set(),
			calendarItems: [],
			startDate: null,
			endDate: null,
			showDate: (this.$route.query['from']) ? Date.parse(this.$route.query['from']) : new Date(),
			showDateOverwritten: false,
		}
	},
	computed: {
		computedShowDate() {
			if (this.showDateOverwritten) {
				return this.showDate
			}

			return (this.$route.query['from']) ? Date.parse(this.$route.query['from']) : new Date()
		}
	},
	methods: {
		setShowDate(d) {
			this.showDateOverwritten = true
			this.showDate = d;
		},
		async periodChangeCallback(data) {
			this.startDate = data.value.periodStart.value
			this.endDate = data.value.periodEnd.value

			await this.$emit(
				'updateFilters',
				{
					from: this.dateToString(this.startDate),
					to: this.dateToString(this.endDate),
					'per-page': 100,
					'events-page': 1
				},
				true
			)
		},
		generateUniqueColour() {
			let colour;

			const firstFive = [
				'#DD2D6A',
				'#532E52',
				'#F9F871',
				'#1A7578',
				'#7674AF'
			]

			if (this.usedColours.size < 5) {
				// Use default colours first.
				colour = firstFive[this.usedColours.size]
			} else {
				// Generate a random colour until it is unique.
				do {
					colour = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
				} while (this.usedColours.has(colour));
			}

			// Add the new colour to the set.
			this.usedColours.add(colour);

			// Determine the contrasting text colour.
			const textColour = this.getContrastingTextColour(colour);

			return { backgroundColour: colour, textColour: textColour };
		},
		getContrastingTextColour(hexColour) {
			// Convert hex to RGB.
			const r = parseInt(hexColour.slice(1, 3), 16);
			const g = parseInt(hexColour.slice(3, 5), 16);
			const b = parseInt(hexColour.slice(5, 7), 16);

			// Calculate the brightness based on the YIQ formula
			const brightness = (r * 299 + g * 587 + b * 114) / 1000;

			// Return white for dark backgrounds and black for light backgrounds
			return brightness > 128 ? '#000000' : '#FFFFFF';
		},
		onClickItem(e) {
			if (e.url) {
				window.open(e.url, '_blank');
			}
		},
		dateToString(date) {
			if (!date) {
				return null
			}

			const d = new Date(date)

			if (isNaN(d)) {
				return d
			}

			return date.getFullYear() + '-' + ((1 + date.getMonth() < 10) ? '0' : '') + (1 + date.getMonth()) + '-' + ((date.getDate() < 10 ? '0' : '')) + date.getDate()
		},
		decodeHtml(html) {
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');
			return doc.documentElement.textContent;
		}
	},
	watch: {
		'$route.query.from'(newValue) {
			this.showDateOverwritten = false
			this.showDate = Date.parse(newValue);
		},
		events() {
			this.calendarItems = [];

			if (this.events.length) {
				for (let i = 0; i < this.events.length; i++) {
					// const { backgroundColour, textColour } = this.generateUniqueColour();

					const backgroundColour = '#DD2D6A';
					const textColour = '#FFFFFF';

					if ('object' === typeof this.events[i].field_instances && this.events[i].field_instances.length) {
						for (let k = 0; k < this.events[i].field_instances.length; k++) {
							this.calendarItems.push(
								{
									id: this.events[i].id + '-' + this.events[i].field_instances[k].id,
									startDate: Date.parse(this.events[i].field_instances[k].start_date),
									endDate: Date.parse(this.events[i].field_instances[k].start_date),
									title: this.events[i].title,
									tooltip: this.decodeHtml(this.events[i].title + ' - ' + this.events[i].excerpt),
									url: this.events[i].link,
									classes: ['calendar-event', 'calendar-event-instance'],
									style: 'background-color:' + backgroundColour + '; color: ' + textColour + ';',
								}
							)
						}
					} else {
						this.calendarItems.push(
							{
								id: this.events[i].id,
								startDate: Date.parse(this.events[i].field_event_start_date),
								endDate: Date.parse(this.events[i].field_event_end_date),
								title: this.events[i].title,
								tooltip: this.decodeHtml(this.events[i].title + ' - ' + this.events[i].excerpt),
								url: this.events[i].link,
								classes: ['calendar-event'],
								style: 'background-color:' + backgroundColour + '; color: ' + textColour + ';',
							}
						)
					}
				}
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.calendar-view {
	background-color: #FFF;
}

:deep(.cv-wrapper) {

	&.period-month .cv-week,
	&.period-year .cv-week {
		min-height: 11rem;
	}

	.calendar-event {

		cursor: pointer;

		a,
		.startTime,
		.endTime {
			color: inherit !important;
		}

	}

	.cv-header-nav {

		.nextYear,
		.previousYear {
			display: none;
		}
	}

	.cv-item {
		white-space: normal;
	}
}
</style>
